/**
 * Admin-side controls for the referral program.
 *
 * Two sections:
 *   1. <ReferralActionsForSubmission> -- inline controls inside a submission's
 *      expanded row (set estimated savings, mark accepted/paid/refunded).
 *      Each action updates the submission + any linked referral and fires
 *      the matching stage-transition email (R2/R3/R6).
 *
 *   2. <ReferralPayouts> -- a standalone table of referrers with non-zero
 *      claimable balance. Each row has a Mark Paid form (method, handle,
 *      reference) that settles every qualified referral for that referrer
 *      and fires R5.
 *
 * These deliberately do not duplicate the submissions list above; they extend
 * it. Callers stitch both into AdminView.
 */

import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { sendReferralStageEmail } from "@/lib/email";
import { generateReferrerCode } from "@/lib/referralCode";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ReferralRow {
  id: string;
  referrer_id: string;
  referrer_code: string;
  stage: string;
  commission_rate: number;
  commission_amount_usd: number | null;
  estimated_savings_usd: number | null;
  referred_name: string | null;
  referred_email: string | null;
}

interface ReferrerMinimal {
  id: string;
  code: string;
  name: string;
  email: string;
}

interface BalanceRow {
  referrer_id: string;
  referrer_code: string;
  name: string;
  email: string;
  payout_method: string | null;
  payout_handle: string | null;
  claimable_usd: number;
  pending_usd: number;
  lifetime_paid_count: number;
  lifetime_paid_usd: number;
  lifetime_referred_count: number;
}

// ---------------------------------------------------------------------------
// Inline submission-scoped controls
// ---------------------------------------------------------------------------

interface Props {
  submissionId: string;
  submission: {
    full_legal_name?: string;
    estimated_savings_usd?: number | null;
    accepted_at?: string | null;
    paid_at?: string | null;
    refunded_at?: string | null;
  };
  onUpdated: (patch: Record<string, unknown>) => void;
}

export function ReferralActionsForSubmission({ submissionId, submission, onUpdated }: Props) {
  const [savingsInput, setSavingsInput] = useState<string>(
    submission.estimated_savings_usd != null ? String(submission.estimated_savings_usd) : ""
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linkedReferral, setLinkedReferral] = useState<ReferralRow | null>(null);
  const [referrer, setReferrer] = useState<ReferrerMinimal | null>(null);

  // Fetch any referral row linked to this submission so the admin can see
  // commission projections alongside the controls.
  useEffect(() => {
    let cancelled = false;
    async function fetchLink() {
      const { data: ref } = await supabase
        .from("referrals")
        .select("id, referrer_id, referrer_code, stage, commission_rate, commission_amount_usd, estimated_savings_usd, referred_name, referred_email")
        .eq("source", "intake")
        .eq("source_submission_id", submissionId)
        .maybeSingle();
      if (cancelled) return;
      setLinkedReferral(ref as ReferralRow | null);

      if (ref) {
        const { data: r } = await supabase
          .from("referrers")
          .select("id, code, name, email")
          .eq("id", ref.referrer_id)
          .maybeSingle();
        if (!cancelled) setReferrer(r as ReferrerMinimal | null);
      }
    }
    fetchLink();
    return () => {
      cancelled = true;
    };
  }, [submissionId]);

  async function handleSetSavings() {
    const parsed = parseFloat(savingsInput);
    if (!isFinite(parsed) || parsed < 0) {
      setError("Enter a non-negative number.");
      return;
    }
    setError(null);
    setBusy("savings");
    try {
      const nowIso = new Date().toISOString();

      const { error: subErr } = await supabase
        .from("submissions")
        .update({ estimated_savings_usd: parsed, audit_completed_at: nowIso })
        .eq("id", submissionId);
      if (subErr) throw subErr;

      if (linkedReferral) {
        // Re-read the CURRENT stage from the database before deciding whether
        // to fire R2. Without this, a second "Save savings" click would fire
        // R2 again because the in-memory linkedReferral.stage is stale.
        const { data: fresh } = await supabase
          .from("referrals")
          .select("stage")
          .eq("id", linkedReferral.id)
          .maybeSingle();
        const currentStage = fresh?.stage ?? linkedReferral.stage;

        // Always mirror the updated savings onto the referral so commission
        // stays in sync if the admin revises the number.
        const updatePayload: Record<string, unknown> = { estimated_savings_usd: parsed };

        // Only transition (and only then fire R2) if we're still at signed_up.
        const shouldTransition = currentStage === "signed_up";
        if (shouldTransition) {
          updatePayload.stage = "savings_quoted";
          updatePayload.savings_quoted_at = nowIso;
        }

        const { error: refErr } = await supabase
          .from("referrals")
          .update(updatePayload)
          .eq("id", linkedReferral.id);
        if (refErr) throw refErr;

        // Keep local state in sync so subsequent actions see fresh data.
        setLinkedReferral({
          ...linkedReferral,
          stage: shouldTransition ? "savings_quoted" : currentStage,
          estimated_savings_usd: parsed,
        });

        if (shouldTransition && referrer) {
          const commission = parsed * linkedReferral.commission_rate;
          sendReferralStageEmail("savings_quoted", {
            referrerEmail: referrer.email,
            referrerName: referrer.name,
            referrerCode: referrer.code,
            referralIds: [linkedReferral.id],
            referredFirstName: linkedReferral.referred_name?.split(" ")[0] || submission.full_legal_name?.split(" ")[0] || "your friend",
            estimatedSavingsUsd: parsed,
            commissionUsd: commission,
          }).catch((e) => console.error("[Admin] R2 send failed", e));
        }
      }
      onUpdated({ estimated_savings_usd: parsed, audit_completed_at: nowIso });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setBusy(null);
    }
  }

  async function handleMarkPaid() {
    setError(null);
    setBusy("paid");
    try {
      const nowIso = new Date().toISOString();
      const { error: subErr } = await supabase
        .from("submissions")
        .update({ paid_at: nowIso })
        .eq("id", submissionId);
      if (subErr) throw subErr;

      // The DB trigger (on_submission_paid) has already cascaded paid_at into
      // the linked referrals row. We only need to fire the R3 email here.
      if (linkedReferral && referrer) {
        const savings = linkedReferral.estimated_savings_usd ?? submission.estimated_savings_usd ?? 0;
        const commission = savings * linkedReferral.commission_rate;
        const holdClearsOn = new Date(Date.now() + 30 * 86400 * 1000).toISOString();
        sendReferralStageEmail("friend_paid", {
          referrerEmail: referrer.email,
          referrerName: referrer.name,
          referrerCode: referrer.code,
          referralIds: [linkedReferral.id],
          referredFirstName: linkedReferral.referred_name?.split(" ")[0] || submission.full_legal_name?.split(" ")[0] || "your friend",
          estimatedSavingsUsd: savings || undefined,
          commissionUsd: commission || undefined,
          holdClearsOn,
        }).catch((e) => console.error("[Admin] R3 send failed", e));
      }
      onUpdated({ paid_at: nowIso });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to mark paid");
    } finally {
      setBusy(null);
    }
  }

  async function handleMarkRefunded() {
    if (!confirm("Mark this submission as refunded? The linked referral (if any) will be voided.")) return;
    setError(null);
    setBusy("refunded");
    try {
      const nowIso = new Date().toISOString();
      const { error: subErr } = await supabase
        .from("submissions")
        .update({ refunded_at: nowIso })
        .eq("id", submissionId);
      if (subErr) throw subErr;

      if (linkedReferral && referrer) {
        sendReferralStageEmail("void", {
          referrerEmail: referrer.email,
          referrerName: referrer.name,
          referrerCode: referrer.code,
          referralIds: [linkedReferral.id],
          referredFirstName: linkedReferral.referred_name?.split(" ")[0] || "your friend",
          voidReason: "customer_refunded",
        }).catch((e) => console.error("[Admin] R6 send failed", e));
      }
      onUpdated({ refunded_at: nowIso });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to mark refunded");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-4">
      <h4 className="text-sm font-semibold text-slate-900 mb-3">Referral & audit actions</h4>

      {error && <p className="text-sm text-red-700 mb-2">{error}</p>}

      {linkedReferral ? (
        <div className="mb-3 text-sm">
          <span className="inline-block px-2 py-0.5 rounded bg-teal-100 text-teal-800 text-xs font-medium">
            Linked to referrer {linkedReferral.referrer_code}
          </span>
          <span className="ml-2 text-slate-600">
            Stage: <strong>{linkedReferral.stage}</strong>
            {linkedReferral.commission_amount_usd != null && (
              <> &middot; Commission: ${Number(linkedReferral.commission_amount_usd).toFixed(2)}</>
            )}
          </span>
        </div>
      ) : (
        <p className="mb-3 text-sm text-slate-600">No referrer attributed to this submission.</p>
      )}

      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-slate-700 mb-1">Estimated annual savings (USD)</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              step="0.01"
              value={savingsInput}
              onChange={(e) => setSavingsInput(e.target.value)}
              disabled={busy !== null}
              className="px-2 py-1 border border-slate-300 rounded text-sm w-32"
              placeholder="400.00"
            />
            <button
              onClick={handleSetSavings}
              disabled={busy !== null}
              className="px-3 py-1 bg-teal-600 text-white rounded text-sm font-medium disabled:opacity-50"
            >
              {busy === "savings" ? "Saving..." : "Save savings"}
            </button>
          </div>
        </div>

        <button
          onClick={handleMarkPaid}
          disabled={busy !== null || submission.paid_at != null}
          className="px-3 py-1 bg-emerald-600 text-white rounded text-sm font-medium disabled:opacity-50"
          title={submission.paid_at != null ? `Already marked paid at ${submission.paid_at}` : ""}
        >
          {submission.paid_at != null ? "Paid ✓" : busy === "paid" ? "Marking..." : "Mark friend paid"}
        </button>

        <button
          onClick={handleMarkRefunded}
          disabled={busy !== null || submission.refunded_at != null}
          className="px-3 py-1 bg-red-100 text-red-800 border border-red-300 rounded text-sm font-medium disabled:opacity-50"
        >
          {submission.refunded_at != null ? "Refunded ✓" : busy === "refunded" ? "Marking..." : "Mark refunded"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payouts table
// ---------------------------------------------------------------------------

export function ReferralPayouts() {
  const [rows, setRows] = useState<BalanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const { data, error: err } = await supabase
      .from("referrer_balances")
      .select("*")
      .order("claimable_usd", { ascending: false });
    if (err) {
      setError(err.message);
    } else {
      setRows((data || []) as BalanceRow[]);
    }
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  if (loading) return <p className="text-sm text-slate-600">Loading payouts...</p>;
  if (error) return <p className="text-sm text-red-700">{error}</p>;

  const withBalance = rows.filter((r) => r.claimable_usd > 0);

  return (
    <div className="space-y-6">
      <CreateReferrerForm onCreated={load} />

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Referral payouts</h2>
          <button onClick={load} className="text-sm text-teal-600 hover:text-teal-700 font-medium">
            Refresh
          </button>
        </div>

      {withBalance.length === 0 ? (
        <div className="px-6 py-8 text-center text-slate-600 text-sm">
          No claimable balances right now.
        </div>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900">Referrer</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900">Method / Handle</th>
              <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">Claimable</th>
              <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">Pending</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900">Action</th>
            </tr>
          </thead>
          <tbody>
            {withBalance.map((r) => (
              <PayoutRow key={r.referrer_id} row={r} onDone={load} />
            ))}
          </tbody>
        </table>
      )}
      </div>
    </div>
  );
}

function CreateReferrerForm({ onCreated }: { onCreated: () => void }) {
  // Admin-only inline form for onboarding a new referrer. The code can be
  // auto-generated (default) or entered manually for human-friendly codes
  // like SLACK_PILOT. Uses the admin's Supabase session (RLS grants full
  // access when app_metadata.is_admin = true) so no new endpoint is needed.
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(() => generateReferrerCode());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ code: string; url: string } | null>(null);

  function regenerate() {
    setCode(generateReferrerCode());
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedCode = code.trim();
    if (!trimmedName) return setError("Name is required");
    if (!trimmedEmail.includes("@")) return setError("Valid email is required");
    if (!/^[A-Za-z0-9_-]{4,40}$/.test(trimmedCode)) {
      return setError("Code must be 4-40 chars, letters/digits/underscore/hyphen only");
    }

    setBusy(true);
    try {
      const { error: insErr } = await supabase.from("referrers").insert({
        code: trimmedCode,
        name: trimmedName,
        email: trimmedEmail,
        source: "manual",
      });
      if (insErr) {
        if (insErr.code === "23505") {
          setError(`Code "${trimmedCode}" is already in use. Regenerate or pick another.`);
        } else {
          setError(insErr.message);
        }
        return;
      }
      setCreated({
        code: trimmedCode,
        url: `https://onecalla.com/?r=${encodeURIComponent(trimmedCode)}`,
      });
      setName("");
      setEmail("");
      setCode(generateReferrerCode());
      onCreated();
    } finally {
      setBusy(false);
    }
  }

  async function copyUrl(url: string) {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* older browser / denied permission */
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
      <h2 className="text-lg font-semibold text-slate-900 mb-4">Create referrer</h2>

      {created && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-emerald-900 font-medium mb-2">
            Created. Share this link:
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-2 py-1 bg-white border border-emerald-200 rounded text-sm font-mono break-all">
              {created.url}
            </code>
            <button
              type="button"
              onClick={() => copyUrl(created.url)}
              className="px-3 py-1 bg-emerald-600 text-white rounded text-sm font-medium"
            >
              Copy
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs text-slate-700 mb-1">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            placeholder="Alex Chen"
            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-700 mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
            placeholder="alex@example.com"
            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-700 mb-1">
            Code{" "}
            <button
              type="button"
              onClick={regenerate}
              disabled={busy}
              className="ml-2 text-teal-600 hover:text-teal-700 underline text-xs"
            >
              Regenerate
            </button>
          </label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={busy}
            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm font-mono"
          />
        </div>

        {error && (
          <p className="md:col-span-3 text-sm text-red-700">{error}</p>
        )}

        <div className="md:col-span-3 flex justify-end">
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-1.5 bg-teal-600 text-white rounded text-sm font-medium disabled:opacity-50"
          >
            {busy ? "Creating..." : "Create referrer"}
          </button>
        </div>
      </form>
    </div>
  );
}

function PayoutRow({ row, onDone }: { row: BalanceRow; onDone: () => void }) {
  const [method, setMethod] = useState(row.payout_method || "zelle");
  const [handle, setHandle] = useState(row.payout_handle || "");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function markPaid() {
    if (!handle.trim()) return setError("Enter the handle you sent to.");
    if (!reference.trim()) return setError("Enter the confirmation / reference number.");
    setBusy(true);
    setError(null);

    try {
      const nowIso = new Date().toISOString();

      // Persist payout method on the referrer for future claims.
      await supabase
        .from("referrers")
        .update({ payout_method: method, payout_handle: handle })
        .eq("id", row.referrer_id);

      // Fetch the individual referrals we're settling so the receipt email
      // can name them.
      const { data: qualified, error: qErr } = await supabase
        .from("referrals")
        .select("id, referred_name, attributed_at, commission_amount_usd")
        .eq("referrer_id", row.referrer_id)
        .eq("stage", "qualified");
      if (qErr) throw qErr;
      if (!qualified || qualified.length === 0) {
        throw new Error("No qualified referrals found for this referrer.");
      }

      const ids = qualified.map((q) => q.id);
      const { error: updErr } = await supabase
        .from("referrals")
        .update({
          stage: "paid",
          paid_at: nowIso,
          payout_reference: reference,
        })
        .in("id", ids);
      if (updErr) throw updErr;

      // Bump the referrer's YTD for W-9 thresholding.
      await supabase
        .from("referrers")
        .update({ total_paid_ytd: row.lifetime_paid_usd + row.claimable_usd })
        .eq("id", row.referrer_id);

      // Send R5 receipt. Passing all settled ids so the server can stamp
      // last_notified_stage='paid' on each, keeping the cron out of our hair.
      sendReferralStageEmail("paid", {
        referrerEmail: row.email,
        referrerName: row.name,
        referrerCode: row.referrer_code,
        referralIds: ids,
        availableUsd: row.claimable_usd,
        payoutMethod: method,
        payoutHandleMasked: maskHandle(handle),
        payoutReference: reference,
        coveredReferrals: qualified.map((q) => ({
          name: q.referred_name || "Friend",
          referredOn: q.attributed_at || "",
          amount: Number(q.commission_amount_usd) || 0,
        })),
        lifetimeReferred: row.lifetime_referred_count,
        lifetimeEarnedUsd: row.lifetime_paid_usd + row.claimable_usd,
      }).catch((e) => console.error("[Admin] R5 send failed", e));

      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Mark paid failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr className="border-b border-slate-200 align-top">
      <td className="px-6 py-4 text-sm">
        <div className="font-medium text-slate-900">{row.name}</div>
        <div className="text-slate-500 text-xs">{row.email}</div>
        <div className="text-slate-400 text-xs font-mono mt-1">{row.referrer_code}</div>
      </td>
      <td className="px-6 py-4 text-sm">
        <select
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          disabled={busy}
          className="px-2 py-1 border border-slate-300 rounded text-sm mr-2"
        >
          <option value="zelle">Zelle</option>
          <option value="venmo">Venmo</option>
          <option value="paypal">PayPal</option>
          <option value="cashapp">Cash App</option>
          <option value="other">Other</option>
        </select>
        <input
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          placeholder="email / phone / @handle"
          disabled={busy}
          className="px-2 py-1 border border-slate-300 rounded text-sm w-56"
        />
      </td>
      <td className="px-6 py-4 text-sm text-right font-semibold text-emerald-700">
        ${row.claimable_usd.toFixed(2)}
      </td>
      <td className="px-6 py-4 text-sm text-right text-slate-500">
        ${row.pending_usd.toFixed(2)}
      </td>
      <td className="px-6 py-4 text-sm">
        <input
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="Confirmation #"
          disabled={busy}
          className="px-2 py-1 border border-slate-300 rounded text-sm w-40 mb-1 block"
        />
        <button
          onClick={markPaid}
          disabled={busy}
          className="px-3 py-1 bg-emerald-600 text-white rounded text-sm font-medium disabled:opacity-50"
        >
          {busy ? "Sending..." : "Mark paid"}
        </button>
        {error && <p className="text-xs text-red-700 mt-1">{error}</p>}
      </td>
    </tr>
  );
}

function maskHandle(h: string): string {
  if (h.includes("@")) {
    const [local, domain] = h.split("@");
    return `${local.slice(0, 3)}...@${domain}`;
  }
  if (h.length > 5) return `${h.slice(0, 3)}...${h.slice(-2)}`;
  return h;
}
